import {
  ADMIN_METRIC_ACCENT_TEXT,
  ADMIN_PALETTE,
} from '@/config/adminChartTheme';
import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import ChartCard from '../ChartCard';
import { fetchRevenueTrend } from '../../../services/campaignAnalyticsService';
import millify from 'millify';

interface TrendPoint {
  label: string;
  gmv: number;
  cashbackPaid: number;
  submissions: number;
}

interface RevenueTrendData {
  trend: TrendPoint[];
  totals: { gmv: number; cashbackPaid: number; roas: number };
}

interface GmvRevenueChartProps {
  refreshToken?: number;
}

const GmvRevenueChart: React.FC<GmvRevenueChartProps> = ({ refreshToken }) => {
  const [data, setData] = useState<RevenueTrendData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRevenueTrend()
      .then(setData)
      .finally(() => setLoading(false));
  }, [refreshToken]);

  const series: ApexAxisChartSeries = [
    {
      name: 'GMV',
      type: 'area',
      data: data?.trend.map((d) => d.gmv) ?? [],
    },
    {
      name: 'Cashback Paid',
      type: 'line',
      data: data?.trend.map((d) => d.cashbackPaid) ?? [],
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      fontFamily: 'Inter, sans-serif',
      background: 'transparent',
      animations: { enabled: true, easing: 'easeinout', speed: 600 },
    },
    colors: [ADMIN_PALETTE.primary, ADMIN_PALETTE.accent],
    stroke: { curve: 'smooth', width: [2, 2] },
    fill: {
      type: ['gradient', 'solid'],
      gradient: {
        shade: 'light',
        type: 'vertical',
        opacityFrom: 0.15,
        opacityTo: 0.01,
      },
    },
    xaxis: {
      categories: data?.trend.map((d) => d.label) ?? [],
      labels: {
        style: { colors: '#9B919D', fontSize: '11px' },
        rotate: 0,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9B919D', fontSize: '11px' },
        formatter: (v) => `€${millify(v)}`,
      },
    },
    grid: {
      borderColor: '#F0F0F0',
      strokeDashArray: 4,
      padding: { left: 8, right: 8 },
    },
    tooltip: {
      theme: 'light',
      y: { formatter: (v) => `€${v.toLocaleString()}` },
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      fontSize: '12px',
      labels: { colors: '#6B778C' },
      markers: { shape: 'circle' },
    },
    dataLabels: { enabled: false },
    markers: { size: 0, hover: { size: 4 } },
  };

  const totals = data?.totals;

  return (
    <ChartCard
      title="GMV & Cashback Trend"
      subtitle="Last 12 months performance"
      loading={loading}
      actions={
        totals && (
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-[#9B919D]">Total GMV</p>
              <p className="text-sm font-bold text-[#172B4D]">
                €{millify(totals.gmv)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#9B919D]">ROAS</p>
              <p className={`text-sm font-bold ${ADMIN_METRIC_ACCENT_TEXT}`}>
                {totals.roas}x
              </p>
            </div>
          </div>
        )
      }
    >
      <div style={{ height: 300 }}>
        {data?.trend.length ? (
          <ReactApexChart
            options={options}
            series={series}
            type="line"
            height="100%"
            width="100%"
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-[#9B919D] text-sm">No revenue data available</p>
          </div>
        )}
      </div>
    </ChartCard>
  );
};

export default GmvRevenueChart;
